/* eslint-disable no-param-reassign */
/* eslin-disable no-cycle */
import axios from 'axios';
import Vue from 'vue';
import * as Sentry from '@sentry/vue';
import { VITE_ENV_REVIEW } from '@/app/data/general_constants';
import store from '@/store/index';
import { ToastProgrammatic as Toast } from 'buefy';
import i18n from '@/i18n/index';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import * as authWrapper from '@/auth';
import axiosRetry from 'axios-retry';
import { ZIGGU_API_URL } from '@/app/util/urls';

const api = axios.create({
  baseURL: ZIGGU_API_URL,
});

// https://github.com/softonic/axios-retry/issues/87
const retryDelay = (retryNumber = 0) => {
  const seconds = (2 ** retryNumber) * 1000;
  const randomMs = 1000 * Math.random();
  return seconds + randomMs;
};

axiosRetry(
  api,
  {
    retries: 3,
    retryDelay,
  },
);

// create a global api event bus for handling specific errors in the app itself
const $apiBus = new Vue();
Vue.prototype.$apiBus = $apiBus;

function validate_constraints(config) {
  // disable manipulative actions by employees during impersonation of customer
  const { current_is_impersonation } = store.getters;
  const { current_profile_true } = store.getters;
  if (current_is_impersonation && current_profile_true
      && current_profile_true.type === PROFILE_TYPES.EMPLOYEE && config.method !== 'get') {
    Toast.open({
      message: i18n.t('toast.action.impersonate_unauthorized'),
      type: 'is-warning',
      duration: 10000,
    });
    const error = new Error('Forbidden action during impersonate.');
    error.sentry_discard = true;
    throw error;
  }
}

// WARNING: should be kept in sync with Dropzone component as well (outside of axios' scope)
api.interceptors.request.use(
  async (config) => {
    validate_constraints(config);
    // create a new object off of config.params
    let config_params = { ...config.params };
    const access_token = await authWrapper.getInstance().getToken();
    // add authorization header with JWT authentication token
    config.headers.authorization = `Bearer ${access_token}`;
    // add app version header based on version attribute incremented on every deploy
    config.headers['X-App-Version'] = `${import.meta.env.RELEASE_VERSION}`;
    // append (relevant) meta params to request's parameters
    // (1) add 'subdomain' meta param to uniquely identify tenant
    let _subdomain = null;
    const parts = window.location.host.split('.');
    if (parts.length > 3
        && import.meta.env.VITE_ENV === VITE_ENV_REVIEW
        && !import.meta.env.IS_LOCAL) {
      [, _subdomain] = parts;
    } else if (parts.length === 3) {
      [_subdomain] = parts;
    }
    if (_subdomain) {
      config_params = Object.assign(config_params || {}, { _subdomain });
    }

    // (2) add 'user_id' meta param to request in development mode
    if (import.meta.env.IS_LOCAL) {
      const _user_id = localStorage.getItem('_user_id');
      config_params = Object.assign(config_params || {}, { _user_id });
    }
    // (3) profile impersonation, add '_profile_id' and '_profile_type' meta params
    const _profile_id = localStorage.getItem('_profile_id');
    const _profile_type = localStorage.getItem('_profile_type');
    const profile = store.getters.current_profile;
    if (_profile_id && _profile_type) {
      // verify profile identical between vuex <-> localStorage, can go out of sync due to different scope:
      //  scope vuex: single tab
      //  scope localStorage: key-value pairs persist window and browser lifetimes and
      //    shared across every window or tab running at the same origin (regardless of path)
      if (profile && (profile.id !== _profile_id || profile.type !== _profile_type)) {
        // mismatch profile between vuex <-> localStorage, needs to trigger app reload to reset state
        //  e.g. multiple simultaneously opened tabs, switch profile in one tab, other tab(s) are out of sync
        window.location.reload(true);
      } else {
        config_params = Object.assign(config_params || {}, { _profile_id, _profile_type });
      }
    }
    config.params = config_params;
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    // verify cache integrity by comparing "current" object between API (via custom HTTP header) and APP (via vuex)
    const timestamps_api = _.chain(response.headers['current-integrity'])
      .split(',')
      .map((timestamp) => [timestamp.split('=')[0], parseInt(timestamp.split('=')[1], 10)])
      .fromPairs()
      .value();
    const timestamps_app = _.chain(store.getters.current_integrity)
      .split(',')
      .map((timestamp) => [timestamp.split('=')[0], parseInt(timestamp.split('=')[1], 10)])
      .fromPairs()
      .value();
    if (timestamps_app['user.logout_at'] < timestamps_api['user.logout_at']) {
      $apiBus.$emit('invalidateSession');
    }
    if (timestamps_app['engage.released_at'] < timestamps_api['engage.released_at']) {
      $apiBus.$emit('invalidateApp');
    }
    if (timestamps_app['client.updated_at'] < timestamps_api['client.updated_at']) {
      $apiBus.$emit('fetchClient');
    }
    if (timestamps_app['profile.notified_at'] < timestamps_api['profile.notified_at']) {
      $apiBus.$emit('fetchProfile');
      $apiBus.$emit('fetchNotificationSubgroups');
    } else if (timestamps_app['profile.updated_at'] < timestamps_api['profile.updated_at']) {
      $apiBus.$emit('fetchProfile');
    }
    return response;
  },
  (error) => {
    // let app handle unauthorized errors
    if (error.response !== undefined && error.response.status === 401) {
      $apiBus.$emit('errors:401');
      throw error;
    // } else if (error.response !== undefined && error.response.status === 500) {
    //   Sentry.captureException(error);
    //   $apiBus.$emit('errors:500');
    //   throw error;
    // let app handle forbidden errors but do propagate error to Sentry (as this shouldn't happen)
    } else if (error.response !== undefined && error.response.status === 403) {
      Sentry.captureException(error);
      $apiBus.$emit('errors:403');
      throw error;
    // error which likely indicates access was revoked to a resource
    } else if (error.response !== undefined && error.response.status === 404) {
      $apiBus.$emit('errors:404');
    // error without a response can e.g. be an unauthorized preflight OPTION request
    } else {
      // propagate other errors (to Sentry)
      throw error;
    }
  },
);

export default api;
